import React from 'react';
import { FlattenSimpleInterpolation } from 'styled-components';

import MobileOnly from 'shared/components/mobile-only';
import DesktopOnly from 'shared/components/desktop-only';
import { useLoyaltyPassModal } from 'src/components/modals/loyalty-pass';
import { useLoyaltyPass } from './use-loyalty-pass';
import { AppleWalletDesktopButton, AppleWalletMobileButton } from './apple-wallet-button';
import { FooterContainer, DesktopLayout, MobileLayout, TextContent } from './footer.styles';
import { LOYALTY_PASS_DESCRIPTION } from './constants';

const MODAL_WIDTH = '470px';

type LoyaltyPassFooterProps = {
  containerOverrides?: FlattenSimpleInterpolation;
  borderOverrides?: FlattenSimpleInterpolation;
};

type LoyaltyPassFooterContentProps = LoyaltyPassFooterProps & {
  loyaltyPassUrl: string;
  loyaltyPassShortUrl: string;
};

/**
 * Internal content component that handles rendering and impression tracking.
 * Only mounted when the loyalty pass should be displayed.
 */
const LoyaltyPassFooterContent: React.FC<LoyaltyPassFooterContentProps> = ({
  containerOverrides,
  borderOverrides,
  loyaltyPassUrl,
  loyaltyPassShortUrl,
}) => {
  const { open } = useLoyaltyPassModal();

  const handleDesktopClick = (): void => {
    void open({
      url: loyaltyPassShortUrl,
      headerText: LOYALTY_PASS_DESCRIPTION,
      width: MODAL_WIDTH,
      closeButtonText: 'Not right now',
      qrCodeSize: 200,
      includeIconRow: true,
    });
  };

  return (
    <FooterContainer
      className='loyalty-pass-footer'
      $containerOverrides={containerOverrides}
      $borderOverrides={borderOverrides}
    >
      <DesktopOnly>
        <DesktopLayout>
          <TextContent>{LOYALTY_PASS_DESCRIPTION}</TextContent>
          <AppleWalletDesktopButton onClick={handleDesktopClick} preventEventBubbling />
        </DesktopLayout>
      </DesktopOnly>

      <MobileOnly>
        <MobileLayout>
          <TextContent>{LOYALTY_PASS_DESCRIPTION}</TextContent>
          <AppleWalletMobileButton href={loyaltyPassUrl} preventEventBubbling />
        </MobileLayout>
      </MobileOnly>
    </FooterContainer>
  );
};

/**
 * Footer banner component for displaying loyalty pass download CTA.
 *
 * This component provides a call-to-action for users to add their loyalty pass
 * to Apple Wallet. It handles device-specific behavior:
 * - Desktop: Opens modal with QR code to scan
 * - iOS Mobile: Direct link to download pass
 * - Android Mobile: Hidden (not supported)
 *
 * The component uses `useAccountLoyaltyPass` hook to determine eligibility and
 * fetch the loyalty pass URL. It will return null if the user is not eligible
 * or if the feature flag is disabled.
 *
 * Background is transparent and inherits from parent container.
 */
export const LoyaltyPassFooter: React.FC<LoyaltyPassFooterProps> = ({ containerOverrides, borderOverrides }) => {
  const { shouldShow, loyaltyPassUrl, loyaltyPassShortUrl, loading } = useLoyaltyPass();

  if (!shouldShow || loading || !loyaltyPassUrl || !loyaltyPassShortUrl) {
    return null;
  }

  return (
    <LoyaltyPassFooterContent
      containerOverrides={containerOverrides}
      borderOverrides={borderOverrides}
      loyaltyPassUrl={loyaltyPassUrl}
      loyaltyPassShortUrl={loyaltyPassShortUrl}
    />
  );
};
